import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ProductStateService,
  ProductAnalyticsService,
} from '@usf/ngrx-product';
import { SearchRequestV2 } from '@usf/product-types';
import { combineLatest, Observable } from 'rxjs';
import {
  debounceTime,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { NewSearchViewModel } from '../models/new-search-view.model';
import { newSearchViewModelSelector } from '../selectors/new-search-page.selector';
import { Params } from '@angular/router';
import { SEARCH_RECORD_PER_BATCH } from '@search/shared/constants/search-config';
import { MerchandisingService } from '@shared/services/merchandising/merchandising.service';
import { ProductService } from '@shared/services/product/product.service';
import { BROWSE_TOP_SELLER_EXPERIENCE } from '@shared/constants/merchandising-constants';
import {
  selectMerchandisingMultipleExperiencesProductNumbers,
  selectMerchandisingViewModel,
} from '@shared/selectors/merchandising.selectors';
import { productByIdSelectorWithSelectedCustomer } from '../../shared/selectors/product.selectors';
import { SearchResultViewModel } from '@search/shared/models/search-view.model';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { UserService } from '@app/user/services';
import { productDetailsViewModelSelector } from '@product-detail/selectors/product-detail.selector';
import { coveoTopSellersViewModelSelector } from '@shared/selectors/coveo-merchandising-selectors';

@Injectable({
  providedIn: 'root',
})
export class NewSearchService {
  constructor(
    protected store: Store,
    private productStateService: ProductStateService,
    private productService: ProductService,
    private merchandisingService: MerchandisingService,
    private userService: UserService,
    private analyticsService: ProductAnalyticsService,
  ) {}

  getSearchRequest(params: Params): SearchRequestV2 {
    const facetFilters = {};
    if (!!params?.facetFilters) {
      const selectedFilters: string[] = params?.facetFilters?.split(',');
      selectedFilters.forEach(filter => {
        const [fieldName, value] = filter?.split(':');

        if (fieldName in facetFilters) {
          facetFilters[fieldName] = [...facetFilters[fieldName], value];
        } else {
          facetFilters[fieldName] = [value];
        }
      });
    }

    return {
      searchText: params.searchText,
      startIndex: 0,
      pageSize: SEARCH_RECORD_PER_BATCH,
      facetFilters,
    };
  }

  executeSearchV2(
    request: SearchRequestV2,
    correlationId: string,
    applyDefaultDeliveryMethodFilter: boolean,
  ): Observable<boolean> {
    return this.productStateService.executeSearchV2(
      request,
      correlationId,
      applyDefaultDeliveryMethodFilter,
    );
  }

  getSearchResult(
    request: SearchRequestV2,
    correlationId: string,
    applyDefaultDeliveryMethodFilter: boolean,
  ): Observable<NewSearchViewModel> {
    return this.executeSearchV2(
      request,
      correlationId,
      applyDefaultDeliveryMethodFilter,
    ).pipe(
      first(isReady => isReady),
      switchMap(() => {
        return this.store.select(newSearchViewModelSelector(correlationId));
      }),
    );
  }

  getTopSellers$() {
    return this.merchandisingService
      .fetchMerchandising({
        experiences: [{ name: BROWSE_TOP_SELLER_EXPERIENCE }],
      })
      .pipe(
        switchMap(_ =>
          this.store.select(
            selectMerchandisingMultipleExperiencesProductNumbers([
              BROWSE_TOP_SELLER_EXPERIENCE,
            ]),
          ),
        ),
        filter(productNumbers => !!productNumbers),
        tap(productNumbers =>
          productNumbers.forEach(productNumberArray =>
            this.productService.loadProducts(productNumberArray),
          ),
        ),
        switchMap(_ =>
          this.store.select(
            selectMerchandisingViewModel(BROWSE_TOP_SELLER_EXPERIENCE),
          ),
        ),
        filter(products => {
          if (products?.length === 0) return false;
          return products?.every(
            val =>
              !!val.summary && !!val.pricing && val.pricing.loading === false,
          );
        }),
      );
  }

  /**
   * Retrieves the base product information to populate the 'original/base' product
   * that needs to be replaced
   * @param replacementBaseProductNumber
   */
  getReplacementBaseProduct(
    replacementBaseProductNumber: number,
  ): Observable<SearchResultViewModel> {
    this.productService.loadProduct(replacementBaseProductNumber);
    return this.store
      .select(
        productByIdSelectorWithSelectedCustomer(replacementBaseProductNumber),
      )
      .pipe(filter(searchVM => !!searchVM.summary && !!searchVM.inventory));
  }

  getTopSellerProducts(): Observable<ProductDetailViewModel[]> {
    return combineLatest([
      this.merchandisingService.loadMerchandisingProducts$(
        BROWSE_TOP_SELLER_EXPERIENCE,
      ),
      this.userService.isGuestUser$(),
    ]).pipe(
      map(([products, isGuestUser]) => {
        return isGuestUser
          ? products?.filter(product =>
              ['0', '1'].includes(product?.inventory?.productStatus),
            )
          : products;
      }),
    );
  }

  fetchAndLoadTopSellers() {
    return this.merchandisingService
      .fetchMerchandising({
        experiences: [{ name: BROWSE_TOP_SELLER_EXPERIENCE }],
      })
      .pipe(
        switchMap(_ => {
          return this.merchandisingService.getMerchandisingProductNumbers$(
            BROWSE_TOP_SELLER_EXPERIENCE,
          );
        }),
        filter(productNumbers => !!productNumbers),
        take(1),
        tap(productNumbersArray => {
          if (productNumbersArray.length > 0) {
            this.productService.loadProducts(productNumbersArray);
          }
        }),
      );
  }

  trackSearchResultPageLoad(
    bannerImageIds: string,
    filters: string,
    searchType?: string,
    productNumbers?: any[],
    searchTerm?: string,
    totalCount?: number,
  ) {
    this.analyticsService.trackSearchResultPageLoad(
      bannerImageIds,
      filters,
      searchType,
      productNumbers,
      searchTerm,
      true,
      totalCount,
    );
  }

  getTopSellersViewModels$(topSellersProductNumbers: number[]) {
    return this.getProductDetails(topSellersProductNumbers);
  }

  private loadProductDetails$(productNumbers: number[]) {
    return this.productService.loadProducts$(productNumbers).pipe(
      first(dispatched => dispatched),
      tap(_ => {
        this.productService.loadProductDetails(productNumbers);
      }),
    );
  }

  getProductDetailViewModels(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<ProductDetailViewModel[]> {
    return this.store
      .select(coveoTopSellersViewModelSelector(productNumbers))
      .pipe(
        filter(productDetails => !!productDetails),
        debounceTime(isDebounce ? 500 : 0),
      );
  }

  getProductDetails(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<ProductDetailViewModel[]> {
    return this.loadProductDetails$(productNumbers).pipe(
      switchMap(() =>
        this.getProductDetailViewModels(productNumbers, isDebounce),
      ),
    );
  }
}
